import { ArrowLeft, Ban, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { QrCodeDisplay } from '@/components/ui/qr-code-display';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useCertificate,
  useRevokeCertificate,
} from '@/features/certificates/queries';
import {
  statusVisualTone,
  type Certificate,
  type CertificateStatus,
} from '@/features/certificates/types';
import { useEquipmentItem } from '@/features/equipment/queries';
import { useVessel } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const STATUS_TONE_CLASS: Record<
  ReturnType<typeof statusVisualTone>,
  string
> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-700',
  muted: 'bg-slate-100 text-slate-700',
};

function StatusBadge({ status, label }: { status: CertificateStatus; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        STATUS_TONE_CLASS[statusVisualTone(status)],
      )}
    >
      {label}
    </span>
  );
}

function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * VesselField / EquipmentField — single-line parent link with safe
 * fallback to the raw id when the lookup hasn't resolved yet.
 */
function ParentField({ cert }: { cert: Certificate }) {
  const { t } = useTranslation();
  const { data: vessel } = useVessel(
    cert.scope === 'vessel' ? cert.vesselId ?? undefined : undefined,
  );
  const { data: equipment } = useEquipmentItem(
    cert.scope === 'equipment' ? cert.equipmentId ?? undefined : undefined,
  );

  if (cert.scope === 'vessel' && cert.vesselId) {
    return (
      <Field
        label={t('certificate.fields.vessel')}
        fullWidth
        value={
          <Link
            to={`/vessels/${cert.vesselId}`}
            className="text-primary hover:underline"
          >
            {vessel ? vesselDisplayName(vessel) : cert.vesselId}
          </Link>
        }
      />
    );
  }
  if (cert.scope === 'equipment' && cert.equipmentId) {
    return (
      <Field
        label={t('certificate.fields.equipment')}
        fullWidth
        value={
          <Link
            to={`/equipment/${cert.equipmentId}`}
            className="text-primary hover:underline"
          >
            {equipment ? (
              <>
                <span className="font-mono text-sm">{equipment.tag}</span>
                <span className="ml-2">{equipment.name}</span>
              </>
            ) : (
              <span className="font-mono">{cert.equipmentId}</span>
            )}
          </Link>
        }
      />
    );
  }
  return <Field label={t('certificate.fields.parent')} value={null} fullWidth />;
}

/**
 * RevokeDialog — captures the mandatory revocation reason then fires
 * the revoke mutation. ConfirmDialog has no slot for free-form input,
 * so we compose Dialog primitives directly. The reason is required by
 * the backend schema (Phase 5a) so we mirror the rule client-side.
 */
function RevokeDialog({
  open,
  onOpenChange,
  certificateId,
  onRevoked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  certificateId: string;
  onRevoked: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const revokeMut = useRevokeCertificate(certificateId);

  const reasonInvalid = touched && reason.trim().length === 0;

  const errorMessage = (() => {
    if (!revokeMut.error) return null;
    if (revokeMut.error instanceof ApiError) {
      return t(revokeMut.error.key, {
        defaultValue: t('certificate.revoke.error'),
        ...(revokeMut.error.params ?? {}),
      });
    }
    return t('certificate.revoke.error');
  })();

  const handleConfirm = () => {
    setTouched(true);
    if (reason.trim().length === 0) return;
    revokeMut.mutate(
      { reason: reason.trim() },
      {
        onSuccess: () => {
          setReason('');
          setTouched(false);
          onRevoked();
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (revokeMut.isPending) return;
      setReason('');
      setTouched(false);
      revokeMut.reset();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('certificate.revoke.title')}</DialogTitle>
          <DialogDescription>
            {t('certificate.revoke.description')}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>{t('certificate.revoke.error')}</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-1">
          <label
            className="text-sm font-medium"
            htmlFor="certificate-revoke-reason"
          >
            {t('certificate.revoke.reasonLabel')}
          </label>
          <textarea
            id="certificate-revoke-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-invalid={reasonInvalid}
            placeholder={t('certificate.revoke.reasonPlaceholder')}
          />
          {reasonInvalid && (
            <p className="text-xs text-destructive">
              {t('certificate.revoke.reasonRequired')}
            </p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={revokeMut.isPending}>
              {t('common.cancel')}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={revokeMut.isPending}
          >
            {revokeMut.isPending && (
              <Loader2 className="animate-spin" aria-hidden />
            )}
            {t('certificate.revoke.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailContent({ cert }: { cert: Certificate }) {
  const { t } = useTranslation();

  const expiryWarn =
    cert.status === 'expiring_soon' || cert.status === 'expired';

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('certificate.detail.sectionIdentity')}>
        <Field
          label={t('certificate.fields.certificateNumber')}
          value={
            <span className="font-mono text-sm">{cert.certificateNumber}</span>
          }
        />
        <Field label={t('certificate.fields.title')} value={cert.title} />
        <Field
          label={t('certificate.fields.issuer')}
          value={t(`certificate.issuer.${cert.issuer}`)}
        />
        <Field
          label={t('certificate.fields.issuerName')}
          value={cert.issuerName}
        />
        <Field
          label={t('certificate.fields.issuingAuthority')}
          value={cert.issuingAuthority}
          fullWidth
        />
        <Field
          label={t('certificate.fields.supersedes')}
          value={
            cert.supersedesCertificateId ? (
              <Link
                to={`/certificates/${cert.supersedesCertificateId}`}
                className="font-mono text-xs text-primary hover:underline"
              >
                {cert.supersedesCertificateId}
              </Link>
            ) : null
          }
          fullWidth
        />
        <Field
          label={t('certificate.fields.notes')}
          value={
            cert.notes ? (
              <p className="whitespace-pre-line">{cert.notes}</p>
            ) : null
          }
          fullWidth
        />
      </Section>

      <Section title={t('certificate.detail.sectionValidity')}>
        <Field
          label={t('certificate.fields.status')}
          value={
            <StatusBadge
              status={cert.status}
              label={t(`certificate.status.${cert.status}`)}
            />
          }
        />
        <Field
          label={t('certificate.fields.issuedAt')}
          value={formatDate(cert.issuedAt)}
        />
        <Field
          label={t('certificate.fields.validFrom')}
          value={formatDate(cert.validFrom)}
        />
        <Field
          label={t('certificate.fields.expiresAt')}
          value={
            <span
              className={cn(
                'tabular-nums',
                expiryWarn && 'font-medium text-red-700',
              )}
            >
              {formatDate(cert.expiresAt) ?? '—'}
            </span>
          }
        />
        {cert.status === 'revoked' && (
          <>
            <Field
              label={t('certificate.fields.revokedAt')}
              value={formatDateTime(cert.revokedAt)}
            />
            <Field
              label={t('certificate.fields.revocationReason')}
              value={
                cert.revocationReason ? (
                  <p className="whitespace-pre-line">{cert.revocationReason}</p>
                ) : null
              }
              fullWidth
            />
          </>
        )}
      </Section>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base">
            {t('certificate.detail.sectionParent')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('certificate.fields.scope')}
              value={t(`certificate.scope.${cert.scope}`)}
            />
            <ParentField cert={cert} />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

export function CertificateDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: cert, isLoading, isError, error, refetch } = useCertificate(id);
  const [revokeOpen, setRevokeOpen] = useState(false);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('certificate.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('certificate.detail.loadError');
  })();

  const canRevoke =
    cert && (cert.status === 'active' || cert.status === 'expiring_soon');

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label={t('common.back')}
          >
            <Link to="/certificates">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {cert ? cert.title : t('common.loading')}
          </h1>
        </div>
        {canRevoke && (
          <Button
            variant="destructive"
            onClick={() => setRevokeOpen(true)}
          >
            <Ban aria-hidden />
            {t('certificate.revoke.button')}
          </Button>
        )}
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('certificate.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {cert && <DetailContent cert={cert} />}

      {cert && <QrCodeDisplay entityType="certificate" entityId={cert.id} />}

      {cert && (
        <RevokeDialog
          open={revokeOpen}
          onOpenChange={setRevokeOpen}
          certificateId={cert.id}
          onRevoked={() => {
            setRevokeOpen(false);
            void refetch();
          }}
        />
      )}
    </div>
  );
}
